import React, { useState, useEffect, useRef, useCallback } from 'react'
import { apiLabs, apiSantoLog } from '../../../Services/api'
import Dashboard from '../../../Components/Dashboard'
import { store } from '../../../store'
import history from '../../../Services/history'
import Denied from '../../../Components/Denied'
import {
  GlobalTransfStyles, PageWrap, Topbar, Body,
  MainCol, StepsCard, StepHeader, StepInput, PairPreview, Feedback, ResetBtn,
  DriverChangeBanner,
  SideCol, SideHeader, SessionStats, SessionStat,
  HistoryList, HistoryEmpty, HistoryItem,
} from './style'
import FeedbackModal, { FeedbackExtra } from '../_shared/FeedbackModal'
import { useAudioUnlock, playErrorBeep, playWarningBeep, playSuccessBeep } from '../_shared/sounds'
import { useScannerInput, useIsCoarsePointer, HIDDEN_INPUT_STYLE } from '../_shared/scanner'
import { normalizeRastreio } from '../_shared/rastreio'

/**
 * Tela de TRANSFERÊNCIA
 *
 * UX: operador bipa um RASTREIO e em seguida o BOX DESTINO. O par é submetido
 * em POST /santolog/transferencia. Cenários tratados:
 *
 *   - Sucesso simples → toast verde, histórico
 *   - Sucesso com driver_changed → banner amarelo informando que o box agora
 *     pertence a outro motorista
 *   - Sucesso no-op (já estava no box) → toast info azul
 *   - 422 com active_volumes_count → modal vermelho explicando que o box
 *     destino tem volumes de outro motorista pendentes (operador precisa
 *     embarcar/devolver primeiro)
 *   - 404 / outros 422 → modal de erro genérico
 */
function fmtTime(d) {
  if (!(d instanceof Date)) d = new Date(d)
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

export default function SantoLogTransferencia(props) {
  const { location: { idPage } } = props
  const [auth, setAuth] = useState(null)

  async function verifyAuth() {
    const { profile } = store.getState().user
    const res = await apiLabs.post('valid_key', { id: profile.id, menu_id: idPage })
    if (res.data.message === 'User not authorized') return setAuth('denied')
    if (res.data.message === 'User authorized') return setAuth('authorized')
    return history.push({ pathname: '/', state: { from: props.location } })
  }

  // Estado do par rastreio + box destino
  const [step, setStep] = useState('rastreio')           // 'rastreio' | 'box'
  const [rastreio, setRastreio] = useState('')
  const [boxBarcode, setBoxBarcode] = useState('')
  const [busy, setBusy] = useState(false)
  const [feedback, setFeedback] = useState({ kind: null, message: '' })
  const [driverChange, setDriverChange] = useState(null) // { from, to } ou null

  const rastreioInputRef = useRef(null)
  const boxInputRef = useRef(null)

  // Histórico da sessão
  const [hist, setHist] = useState([])
  const okCount = hist.filter(h => h.kind === 'ok').length
  const errCount = hist.length - okCount

  // Modal de feedback (error / warning / success / info)
  const initialModal = {
    open: false, kind: 'error', title: '', message: '',
    current_driver: null, active_volumes_count: 0,
  }
  const [modal, setModal] = useState(initialModal)
  const modalOpenRef = useRef(false)
  useEffect(() => { modalOpenRef.current = modal.open }, [modal.open])

  // Destrava AudioContext compartilhado
  useAudioUnlock()

  // Scanner: arma o input ATIVO conforme o step (rastreio/box) sem abrir teclado.
  // armKey=step → re-arma na troca rastreio↔box destino.
  const isCoarse = useIsCoarsePointer()
  const { arm: armScanner } = useScannerInput({
    getInput: () => step === 'rastreio' ? rastreioInputRef.current : boxInputRef.current,
    getDisabled: () => auth !== 'authorized' || modal.open,
    armKey: step,
  })

  function pushHistory(entry) {
    setHist(prev => [{ ...entry, at: new Date() }, ...prev].slice(0, 50))
  }

  // ── INIT ──────────────────────────────────────────────────────────────────
  useEffect(() => { verifyAuth() }, []) // eslint-disable-line

  // Foca o input do passo atual quando entra na tela ou muda de step (via arm — sem teclado)
  useEffect(() => {
    if (auth !== 'authorized') return
    if (modal.open) return
    setTimeout(armScanner, 80)
  }, [auth, step, modal.open, armScanner])

  // Foco perpétuo
  const handleBlur = useCallback(() => {
    setTimeout(() => {
      if (modalOpenRef.current) return
      const ae = document.activeElement
      if (ae && ae !== document.body) {
        const tag = ae.tagName
        if (tag === 'BUTTON' || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
      }
      armScanner()
    }, 50)
  }, [armScanner])

  function resetPair() {
    setRastreio('')
    setBoxBarcode('')
    setStep('rastreio')
    setDriverChange(null)
    setTimeout(armScanner, 50)
  }

  function closeModal() {
    setModal(initialModal)
    setTimeout(armScanner, 30)
  }

  // ── STEP 1: bipa rastreio ─────────────────────────────────────────────────
  function onRastreioEnter() {
    // Normaliza qualquer prefixo (SLEXPRESSO, TMRAPIDO, etc) para ALEF{NF}V{VOL}
    const code = normalizeRastreio(rastreio) || ''
    if (!code) return
    setRastreio(code) // guarda a versão normalizada — o step 2 usa daqui
    setStep('box')
    // armScanner é chamado automaticamente pelo armKey=step
  }

  // ── STEP 2: bipa box destino → POST /transferencia ────────────────────────
  async function onBoxEnter() {
    const boxCode = boxBarcode.trim()
    const rastreioCode = rastreio.trim()
    if (!boxCode || !rastreioCode) return

    setBusy(true)
    setFeedback({ kind: null, message: 'Buscando box destino…' })

    try {
      // 1. Resolve box destino → id
      const boxRes = await apiSantoLog.get(`/box/by-barcode/${encodeURIComponent(boxCode)}`, { params: { module: 'transferencia' } }).catch(err => {
        if (err?.response?.status === 404) return null
        throw err
      })
      if (!boxRes) {
        playErrorBeep()
        setModal({
          open: true, kind: 'error',
          title: 'Box destino não encontrado',
          message: `Nenhum box com código "${boxCode}" foi localizado.`,
        })
        setFeedback({ kind: 'err', message: `Box "${boxCode}" não encontrado` })
        pushHistory({ kind: 'err', rastreio: rastreioCode, box: boxCode, message: 'Box destino não encontrado' })
        resetPair()
        return
      }
      const boxDest = boxRes.data?.box || boxRes.data || {}

      // 2. POST /transferencia
      setFeedback({ kind: null, message: 'Transferindo…' })
      const res = await apiSantoLog.post('/transferencia', {
        box_id: boxDest.id,
        rastreio: rastreioCode,
      })
      const data = res.data || {}
      const box = data.box || {}
      const sourceBox = data.source_box || null
      const driverChanged = !!data.driver_changed
      const isNoOp = data.message && /já está/i.test(data.message)

      if (isNoOp) {
        // No-op: volume já estava no box destino
        playWarningBeep()
        setFeedback({ kind: 'info', message: `⚠ ${rastreioCode} já estava no ${box.name}` })
        pushHistory({
          kind: 'info', rastreio: rastreioCode, box: box.name,
          message: 'Já estava no box (no-op)',
        })
        setModal({
          open: true, kind: 'warning',
          title: 'Volume já estava no box',
          message: `O volume ${rastreioCode} já está no ${box.name}. Nenhuma ação tomada.`,
        })
      } else {
        // Transferência efetiva
        playSuccessBeep()
        if (driverChanged) {
          // Backend mudou o driver do box destino pra absorver o motorista do volume
          setDriverChange({
            box_name: box.name,
            new_driver: box.driver?.name || '—',
            source_box: sourceBox?.name || null,
          })
        }
        const driverPart = box.driver?.name ? ` · motorista ${box.driver.name}` : ''
        const sourcePart = sourceBox?.name ? ` (vindo do ${sourceBox.name})` : ''
        setFeedback({
          kind: 'ok',
          message: `✓ ${rastreioCode} → ${box.name}${driverPart}${sourcePart}`,
        })
        pushHistory({
          kind: 'ok', rastreio: rastreioCode, box: box.name,
          message: driverChanged
            ? `Transferido · box absorveu motorista ${box.driver?.name || ''}`
            : `Transferido${sourceBox?.name ? ` do ${sourceBox.name}` : ''}`,
        })
      }
      resetPair()
    } catch (err) {
      console.error('POST /transferencia failed:', err)
      const data = err?.response?.data || {}
      const errMsg = data.error || data.message || err.message
      const status = err?.response?.status

      // 422 com active_volumes_count → box destino tem volumes de outro motorista
      if (status === 422 && data.active_volumes_count) {
        playErrorBeep()
        setModal({
          open: true, kind: 'error',
          title: 'Box destino ocupado',
          message: errMsg || 'Box destino tem volumes coletados de outro motorista. Embarque ou devolva antes.',
          current_driver: data.current_driver || null,
          active_volumes_count: data.active_volumes_count,
        })
      } else if (status === 404) {
        playErrorBeep()
        setModal({
          open: true, kind: 'error',
          title: 'Não encontrado',
          message: errMsg || 'Volume ou box não localizado',
        })
      } else if (status === 422) {
        // Outros 422 — sem motorista, volume já embarcado, etc → warning
        playWarningBeep()
        setModal({
          open: true, kind: 'warning',
          title: 'Não foi possível transferir',
          message: errMsg || 'Transferência rejeitada pelo backend',
        })
      } else {
        playErrorBeep()
        setModal({
          open: true, kind: 'error',
          title: 'Erro na transferência',
          message: 'Erro: ' + errMsg,
        })
      }
      setFeedback({ kind: 'err', message: errMsg || 'Erro' })
      pushHistory({
        kind: 'err', rastreio: rastreioCode, box: boxCode, message: errMsg,
      })
      resetPair()
    } finally {
      setBusy(false)
    }
  }

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <Dashboard title="Transferência">
      {auth === 'denied' && <Denied />}
      {auth === 'authorized' && (
        <>
          <GlobalTransfStyles />
          <PageWrap>
            <Topbar>
              <h2>🔀 Transferência</h2>
              <span className="crumb">
                Mover um volume de um box pra outro (pode trocar o motorista do box destino)
              </span>
            </Topbar>

            <Body>
              <MainCol>
                <StepsCard>
                  <StepHeader>
                    <div className="step-num">{step === 'rastreio' ? '1' : '2'}</div>
                    <h3>
                      {step === 'rastreio'
                        ? 'Bipe o RASTREIO do volume'
                        : 'Bipe o BOX destino'}
                    </h3>
                    <p>
                      {step === 'rastreio'
                        ? <>Volume que vai ser <strong style={{ color: '#3b82f6' }}>transferido</strong> de box.</>
                        : <>Box pra onde o volume vai. Se o box estiver vazio (sem motorista), ele absorve o motorista do volume.</>}
                    </p>
                  </StepHeader>

                  {step === 'rastreio' ? (
                    <StepInput
                      ref={rastreioInputRef}
                      value={rastreio}
                      $kind={feedback.kind}
                      onChange={e => setRastreio(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') { e.preventDefault(); onRastreioEnter() }
                      }}
                      onBlur={handleBlur}
                      placeholder="ex: ALEF22421V3"
                      disabled={busy}
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck={false}
                      style={isCoarse ? HIDDEN_INPUT_STYLE : undefined}
                    />
                  ) : (
                    <>
                      <PairPreview>
                        <div>
                          <div className="lbl">Volume</div>
                          <div className="val">{rastreio}</div>
                        </div>
                        <span className="arrow">→</span>
                        <div style={{ textAlign: 'right' }}>
                          <div className="lbl">Box destino</div>
                          <div className="val">{boxBarcode || '...'}</div>
                        </div>
                      </PairPreview>
                      <StepInput
                        ref={boxInputRef}
                        value={boxBarcode}
                        $kind={feedback.kind}
                        onChange={e => setBoxBarcode(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') { e.preventDefault(); onBoxEnter() }
                          if (e.key === 'Escape') { e.preventDefault(); resetPair() }
                        }}
                        onBlur={handleBlur}
                        placeholder="Bipe o código do box destino"
                        disabled={busy}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck={false}
                        style={isCoarse ? HIDDEN_INPUT_STYLE : { marginTop: 12 }}
                      />
                    </>
                  )}

                  <Feedback $kind={feedback.kind}>
                    {feedback.message || (busy ? 'Processando…' : 'Aguardando bipe…')}
                  </Feedback>

                  {step === 'box' && (
                    <ResetBtn type="button" onClick={resetPair} disabled={busy}>
                      ← Voltar pro rastreio
                    </ResetBtn>
                  )}
                </StepsCard>

                {/* Banner de driver-changed (alerta de troca de motorista do box) */}
                {driverChange && (
                  <DriverChangeBanner>
                    <span className="icon">🔄</span>
                    <div className="body">
                      <div className="title">Motorista do box atualizado</div>
                      <div className="msg">
                        O <strong>{driverChange.box_name}</strong> agora pertence ao motorista{' '}
                        <strong>{driverChange.new_driver}</strong>.
                        {driverChange.source_box && (
                          <> Volume veio do <strong>{driverChange.source_box}</strong>.</>
                        )}
                      </div>
                    </div>
                  </DriverChangeBanner>
                )}
              </MainCol>

              {/* COL DIREITA: histórico da sessão */}
              <SideCol>
                <SideHeader>
                  <h3>Sessão atual</h3>
                  <p>Histórico das últimas transferências (volátil — perde ao recarregar)</p>
                </SideHeader>
                <SessionStats>
                  <SessionStat $variant="ok">
                    <div className="l">Transferidos</div>
                    <div className="v">{okCount}</div>
                  </SessionStat>
                  <SessionStat $variant="err">
                    <div className="l">Falhas</div>
                    <div className="v">{errCount}</div>
                  </SessionStat>
                </SessionStats>
                <HistoryList>
                  {hist.length === 0 ? (
                    <HistoryEmpty>
                      Nenhuma transferência ainda.<br />
                      Bipe um volume pra começar.
                    </HistoryEmpty>
                  ) : (
                    hist.map((h, i) => (
                      <HistoryItem key={i} $kind={h.kind}>
                        <span className="icon">
                          {h.kind === 'ok' ? '✓' : h.kind === 'info' ? 'i' : '✗'}
                        </span>
                        <div className="info">
                          <div className="rastreio">{h.rastreio}</div>
                          <div className="meta">→ {h.box} · {h.message}</div>
                        </div>
                        <span className="time">{fmtTime(h.at)}</span>
                      </HistoryItem>
                    ))
                  )}
                </HistoryList>
              </SideCol>
            </Body>
          </PageWrap>

          {/* MODAL — feedback (error / warning / success) */}
          <FeedbackModal
            visible={modal.open}
            kind={modal.kind}
            title={modal.title}
            message={modal.message}
            onClose={closeModal}
          >
            {modal.current_driver && (
              <FeedbackExtra>
                <div className="lbl">Motorista atual do box destino</div>
                <div className="val">🚛 {modal.current_driver.name}</div>
                <div className="meta">
                  {modal.active_volumes_count} volume{modal.active_volumes_count !== 1 ? 's' : ''} coletado{modal.active_volumes_count !== 1 ? 's' : ''} pendente{modal.active_volumes_count !== 1 ? 's' : ''}.
                  Embarque ou devolva antes de transferir pra outro motorista.
                </div>
              </FeedbackExtra>
            )}
          </FeedbackModal>
        </>
      )}
    </Dashboard>
  )
}
